import React, { useContext, useEffect, useState } from 'react';
import axios, { all } from 'axios';
import { useNavigate } from 'react-router-dom';
import displayError from '../../utils/ErrorHandler';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import SilatItemsIn from './SilatItemsIn';
import { setActiveZoneId, getActiveZoneId } from '../../utils/zoneSession';
import { n2 } from '../../utils/fmt';
import { formatMoney } from '../../utils/currency';

const ItemIn = () => {
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const [requests, setRequests] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [checkedPOItems, setCheckedPOItems] = useState({});
  const [checkedRequest, setCheckedRequest] = useState({});
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [quantityErrors, setQuantityErrors] = useState({});
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('requests');
  const [grnNumber, setGrnNumber] = useState('');
  const [remarks, setRemarks] = useState('');
  const [showRemarksInput, setShowRemarksInput] = useState(false);
  const [expandedRemarks, setExpandedRemarks] = useState({});
  const navigate = useNavigate();
  const [showOnlyPendingPOs, setShowOnlyPendingPOs] = useState(false);

  useEffect(() => {
    async function startup() {
      console.debug('startup effect running. current user (before auth):', user);
      if (!user) {
        const returnedUser = await authenticate(setUser, navigate);
        console.debug('startup: authenticate returned (value):', returnedUser);
        // quick whoami check
        try {
          const dbg = await axios.get(`${process.env.REACT_APP_API_URI}/api/debug/whoami`, { withCredentials: true });
          console.debug('startup debug/whoami:', dbg.data);
        } catch (dbe) {
          console.debug('startup debug/whoami failed', dbe?.response || dbe.message || dbe);
        }

        if (returnedUser?.zone?._id) {
          setActiveZoneId(returnedUser.zone._id);
        }
      }

      if (user?.zone?._id) {
        setActiveZoneId(user.zone._id);
      } else if (!user) {
        const stored = getActiveZoneId();
        if (stored) {
          setUser((prev) => prev || { zone: { _id: stored }, role: 'Admin' });
        }
      }
    }
    startup();
  }, [user, navigate, setUser]);

  useEffect(() => {
    const fetchData = async () => {
      console.debug('fetchData effect triggered. user:', user);

      // Debug whoami early so we can see token presence even when zoneId missing
      try {
        const dbg = await axios.get(`${process.env.REACT_APP_API_URI}/api/debug/whoami`, { withCredentials: true });
        console.debug('fetchData debug/whoami:', dbg.data);
      } catch (dbe) {
        console.debug('fetchData debug/whoami failed', dbe?.response || dbe.message || dbe);
      }

      const zoneId = user?.zone?._id || getActiveZoneId();
      console.debug('computed zoneId in fetchData:', zoneId);
      if (!zoneId) {
        console.log('No zoneId yet, skipping fetch');
        return;
      }

      try {
        const requestsResponse = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/request/get?id=${zoneId}`,
          { withCredentials: true }
        );
        setRequests(requestsResponse.data.data);
      } catch (err) {
        const errorMsg = displayError(err, navigate, setUser);
        if (errorMsg) setMessage(errorMsg);
      }

      try {
        // Debug: call server-side whoami to confirm token/tenant binding and req.models
        try {
          const dbg = await axios.get(`${process.env.REACT_APP_API_URI}/api/debug/whoami`, { withCredentials: true });
          console.debug('debug/whoami:', dbg.data);
        } catch (dbe) {
          console.debug('debug/whoami failed', dbe?.response || dbe.message || dbe);
        }
        const poResponse = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/po/getPOByZone?id=${zoneId}`,
          { withCredentials: true }
        );
        console.debug('poResponse status', poResponse.status);
        setPurchaseOrders(poResponse.data.data);
      } catch (err) {
        console.error('PO fetch error', err?.response || err);
        const errorMsg = displayError(err, navigate, setUser);
        if (errorMsg) setMessage(errorMsg);
      }
    };

    fetchData();
  }, [user, navigate, setUser]);

  const isFullyReceived = (item) => {
    const requestedQty = Number(item.quantity || 0);
    const receivedQty = Number(item.quantityReceived || 0);
    return requestedQty > 0 && receivedQty >= requestedQty;
  };

  const toggleRemarks = (key) => {
    setExpandedRemarks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getRecordRemarks = (record) => {
    return (record?.remarks || record?.remark || record?.notes || record?.comment || '').trim();
  };

  const getItemRemarks = (item) => {
    return (item?.remarks || item?.remark || item?.notes || item?.comment || '').trim();
  };

  const handleCheckboxChange = (requestId, item, isChecked) => {
    if (isFullyReceived(item)) return;

    setCheckedRequest((prevState) => {
      const newCheckedItems = { ...prevState };
      const key = `${requestId}-${item._id}`;

      if (isChecked) {
        newCheckedItems[key] = {
          poId: requestId,
          itemId: item.item._id,
          name: item.item.name,
          quantity:
            item.status === 'partiallyReceived'
              ? item.quantityDue
              : item.status === 'partiallyDispatched'
              ? item.quantityDispatched
              : item.quantity,
        };
      } else {
        delete newCheckedItems[key];
        const newQuantityErrors = { ...quantityErrors };
        delete newQuantityErrors[key];
        setQuantityErrors(newQuantityErrors);
      }

      return newCheckedItems;
    });
  };

  const handlePOCheckboxChange = (poId, item, isChecked) => {
    if (isFullyReceived(item)) return;

    setCheckedPOItems((prevState) => {
      const newCheckedPOItems = { ...prevState };
      const key = `${poId}-${item._id}`;

      if (isChecked) {
        newCheckedPOItems[key] = {
          poId: poId,
          itemId: item.item._id,
          name: item.item.name,
          quantity:
            item.status === 'partiallyReceived'
              ? item.quantityDue
              : item.quantityReceived || item.quantity || 0,
          price: item.price,
        };
      } else {
        delete newCheckedPOItems[key];
        const newQuantityErrors = { ...quantityErrors };
        delete newQuantityErrors[key];
        setQuantityErrors(newQuantityErrors);
      }

      return newCheckedPOItems;
    });
  };

  const handleQuantityChange = (requestId, itemId, value) => {
    const key = `${requestId}-${itemId}`;

    setCheckedRequest((prevState) => {
      const newCheckedItems = { ...prevState };
      if (newCheckedItems[key]) {
        newCheckedItems[key].quantity = value;
      }
      return newCheckedItems;
    });

    setQuantityErrors((prevState) => {
      const newQuantityErrors = { ...prevState };
      const request = requests.find((r) => r._id === requestId);
      const item = request?.items.find((i) => i._id === itemId);

      if (!item) return prevState;

      const maxQty =
        item.status === 'partiallyReceived'
          ? item.quantityDue
          : item.status === 'partiallyDispatched'
          ? item.quantityDispatched
          : item.quantity;

      if (Number(value) > Number(maxQty)) {
        newQuantityErrors[key] = 'Quantity exceeds available amount';
      } else {
        delete newQuantityErrors[key];
      }

      return newQuantityErrors;
    });
  };

  const handlePOQuantityChange = (poId, itemId, value) => {
    const key = `${poId}-${itemId}`;

    setCheckedPOItems((prevState) => {
      const newCheckedPOItems = { ...prevState };
      if (newCheckedPOItems[key]) {
        newCheckedPOItems[key].quantity = value;
      }
      return newCheckedPOItems;
    });

    setQuantityErrors((prevState) => {
      const newQuantityErrors = { ...prevState };
      if (Number(value) <= 0) {
        newQuantityErrors[key] = 'Quantity must be greater than 0';
      } else {
        delete newQuantityErrors[key];
      }
      return newQuantityErrors;
    });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  let filteredRequests = [];
  if (requests) {
    filteredRequests = requests.filter((request) =>
      request.items.some((item) =>
        item.item.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }

  const filteredPurchaseOrders = purchaseOrders.filter((po) => {
    const matchesSearch = po.items.some((item) =>
      item.item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (!matchesSearch) return false;

    if (!showOnlyPendingPOs) return true;

    return po.items.some((item) => !isFullyReceived(item));
  });

  const checkedItemsCount = Object.keys(checkedItems).length;
  const checkedPOItemsCount = Object.keys(checkedPOItems).length;
  const checkedRequestItemsCount = Object.keys(checkedRequest).length;
  const totalCheckedItems =
    checkedItemsCount + checkedPOItemsCount + checkedRequestItemsCount;

  useEffect(() => {
    if (totalCheckedItems > 0) {
      setIsBannerVisible(true);
    } else {
      const timeoutId = setTimeout(() => {
        setIsBannerVisible(false);
        setShowRemarksInput(false);
        setRemarks('');
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [totalCheckedItems]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-400';
      case 'approved':
        return 'bg-green-500';
      case 'supplyapproved':
        return 'bg-teal-500';
      case 'supplyrequested':
        return 'bg-teal-700';
      case 'rejected':
        return 'bg-red-500';
      case 'dispatched':
        return 'bg-blue-500';
      case 'received':
        return 'bg-green-500';
      case 'partiallyReceived':
        return 'bg-cyan-500';
      default:
        return 'bg-orange-500';
    }
  };

  const addAndUpdate = () => {
    setButtonDisabled(true);

    if (Object.keys(quantityErrors).length > 0.0) {
      setErrorMessage('Please fix quantity errors before submitting.');
      setButtonDisabled(false);
      return;
    }

    if (checkedPOItemsCount > 0 && !grnNumber.trim()) {
      setErrorMessage('Please enter GRN number for purchase order items.');
      setButtonDisabled(false);
      return;
    }

    const trimmedRemarks = remarks.trim();
    const promises = [];

    if (checkedRequestItemsCount > 0) {
      const itemsIn = Object.values(checkedRequest).map((item) => ({
        name: item.name,
        quantity: item.quantity,
        remarks: trimmedRemarks,
      }));

      const updateStatus = Object.values(checkedRequest).map((item) => ({
        requestId: item.poId,
        itemId: item.itemId,
        quantity: item.quantity,
        remarks: trimmedRemarks,
      }));

      const zoneIdForReq = user?.zone?._id || getActiveZoneId();
      const updateRequestPromise = axios
        .patch(
          `${process.env.REACT_APP_API_URI}/api/request/itemsReceived?id=${zoneIdForReq}`,
          { updateStatus, remarks: trimmedRemarks },
          { withCredentials: true }
        )
        .then((updateRequestResponse) => {
          if (updateRequestResponse.status === 200 && updateRequestResponse.data.success) {
            return axios.post(
              `${process.env.REACT_APP_API_URI}/api/stock/add?id=${user?.zone?._id}`,
              { items: itemsIn, remarks: trimmedRemarks },
              { withCredentials: true }
            );
          } else {
            throw new Error('Update request status failed');
          }
        });

      promises.push(updateRequestPromise);
    }

    if (checkedPOItemsCount > 0) {
      const poItemsIn = Object.values(checkedPOItems).map((item) => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        remarks: trimmedRemarks,
      }));

      const updatePOStatus = Object.values(checkedPOItems).map((item) => ({
        poId: item.poId,
        itemId: item.itemId,
        quantity: item.quantity,
        grn: grnNumber.trim(),
        remarks: trimmedRemarks,
      }));
      const zoneIdForReq = user?.zone?._id || getActiveZoneId();

      const updatePOPromise = axios
        .patch(
          `${process.env.REACT_APP_API_URI}/api/po/itemsReceived`,
          {
            updateStatus: updatePOStatus,
            grn: grnNumber.trim(),
            remarks: trimmedRemarks,
          },
          { withCredentials: true }
        )
            .then((updatePOResponse) => {
          if (updatePOResponse.status === 200 && updatePOResponse.data.success) {
            // Pass the current user's zone id so the backend can add items to the correct zone
            return axios.post(
              `${process.env.REACT_APP_API_URI}/api/stock/add?id=${zoneIdForReq}`,
              { items: poItemsIn, remarks: trimmedRemarks },
              { withCredentials: true }
            );
          } else {
            throw new Error('Update purchase order status failed');
          }
        });

      promises.push(updatePOPromise);
    }

    Promise.all(promises)
      .then(() => {
        console.log('All requests completed');
        setSuccessMessage('Items added to Inventory!');
        setErrorMessage('');
        setRemarks('');
        setShowRemarksInput(false);

        setTimeout(() => {
          navigate('/zc/stock');
        }, 1000);
      })
      .catch((error) => {
        setErrorMessage(
          'An error occurred while processing your request. Please try again.'
        );
        setSuccessMessage('');
        setButtonDisabled(false);
        console.error(error);
      });
  };

  const handleSelectAll = (poId, items, isChecked) => {
    const updatedCheckedItems = { ...checkedPOItems };

    items.forEach((item) => {
      const key = `${poId}-${item._id}`;

      if (isChecked && !isFullyReceived(item)) {
        updatedCheckedItems[key] = {
          poId: poId,
          itemId: item.item._id,
          name: item.item.name,
          quantity:
            item.status === 'partiallyReceived'
              ? item.quantityDue
              : item.quantityReceived || item.quantity || 0,
          price: item.price || 0,
        };
      } else {
        delete updatedCheckedItems[key];
      }
    });

    setCheckedPOItems(updatedCheckedItems);
  };

  const handleSelectAllRequest = (poId, items, isChecked) => {
    const updatedCheckedItems = { ...checkedRequest };
    const hasSupplyRequestedStatus = items.some(
      (item) => item.status === 'supplyrequested'
    );

    items.forEach((item) => {
      const key = `${poId}-${item._id}`;

      const shouldSelect =
        isChecked &&
        !isFullyReceived(item) &&
        ((hasSupplyRequestedStatus && item.status === 'supplyrequested') ||
          (!hasSupplyRequestedStatus && !isFullyReceived(item)));

      if (shouldSelect) {
        updatedCheckedItems[key] = {
          poId: poId,
          itemId: item.item._id,
          name: item.item.name,
          quantity:
            item.status === 'partiallyReceived'
              ? item.quantityDue
              : item.quantityReceived || item.quantity || 0,
          price: item.price || 0,
        };
      } else {
        delete updatedCheckedItems[key];
      }
    });

    setCheckedRequest(updatedCheckedItems);
  };

  const hasSupplyRequested = (request) => {
    return request.items.some((item) => item.status === 'supplyrequested');
  };

  return (
    <>
      <div className="bg-lime-800 flex items-center justify-between w-full p-4 text-white fixed z-10">
        <h4 className="text-lg md:text-xl font-semibold text-center flex-grow">
          Items Check In
        </h4>
      </div>

      {message ? (
        <div
          className="bg-red-100 border border-red-300 text-red-500 px-4 py-3 mx-0 rounded relative"
          role="alert"
        >
          <span className="block sm:inline">{message}</span>
          <span className="absolute top-0 bottom-0 right-0 px-4 py-3"></span>
        </div>
      ) : null}

      <div className="p-4 pb-20 pt-[70px]">
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by item name..."
            className="w-full p-2 border border-gray-300 rounded text-sm"
          />
        </div>

        <div className="flex mb-4 border-b">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 'requests'
                ? 'text-lime-800 border-b-2 border-lime-800'
                : 'text-gray-600'
            }`}
            onClick={() => setActiveTab('requests')}
          >
            Requests ({requests.length})
          </button>

          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 'purchase-orders'
                ? 'text-lime-800 border-b-2 border-lime-800'
                : 'text-gray-600'
            }`}
            onClick={() => setActiveTab('purchase-orders')}
          >
            Purchase Orders ({purchaseOrders.length})
          </button>

          {((user?.role && user.role.toLowerCase() === 'admin') ||
            (user?.zone?.desc &&
              user.zone.desc.toLowerCase() === 'central zone')) && (
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === 'silat-items'
                  ? 'text-lime-800 border-b-2 border-lime-800'
                  : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('silat-items')}
            >
              Silat Item`s In
            </button>
          )}
        </div>

        {activeTab === 'requests' && (
          <>
            {filteredRequests.length > 0 ? (
              filteredRequests.map((request, index) => (
                <div
                  key={index}
                  className="p-4 mb-3 border rounded shadow-md bg-white"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-[16px] font-semibold text-gray-700">
                      REQ #00{index + 1}
                    </h2>
                    <h4 className="text-sm text-gray-400">
                      {formatDate(request.date)}
                    </h4>
                  </div>

                  {getRecordRemarks(request) && (
                    <div className="mb-3">
                      <button
                        type="button"
                        onClick={() => toggleRemarks(`request-${request._id}`)}
                        className="inline-flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full hover:bg-amber-100"
                      >
                        <span>Remarks</span>
                        <span className="text-[10px] text-amber-600">
                          {expandedRemarks[`request-${request._id}`]
                            ? 'Hide'
                            : 'View'}
                        </span>
                      </button>

                      {expandedRemarks[`request-${request._id}`] && (
                        <div className="mt-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-gray-700 whitespace-pre-wrap">
                          {getRecordRemarks(request)}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center">
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleSelectAllRequest(
                            request._id,
                            request.items,
                            e.target.checked
                          )
                        }
                        checked={
                          request.items
                            .filter((item) =>
                              hasSupplyRequested(request)
                                ? item.status === 'supplyrequested'
                                : !isFullyReceived(item)
                            )
                            .every(
                              (item) =>
                                checkedRequest[`${request._id}-${item._id}`]
                            ) &&
                          request.items.some((item) =>
                            hasSupplyRequested(request)
                              ? item.status === 'supplyrequested'
                              : !isFullyReceived(item)
                          )
                        }
                        disabled={!request.check}
                      />
                      <span className="checkmark"></span>
                    </label>
                    <span className="ml-2 text-sm font-semibold text-green-600 flex items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Select All
                    </span>
                  </div>

                  {request.items
                    .filter((item) =>
                      item.item.name
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    )
                    .map((item, idx, array) => (
                      <div
                        key={idx}
                        className={`flex items-center mb-2 ${
                          idx < array.length - 1 ? 'border-b pb-2' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <label className="custom-checkbox">
                            <input
                              type="checkbox"
                              disabled={isFullyReceived(item)}
                              checked={
                                checkedRequest[
                                  `${request._id}-${item._id}`
                                ] !== undefined
                              }
                              onChange={(e) =>
                                handleCheckboxChange(
                                  request._id,
                                  item,
                                  e.target.checked
                                )
                              }
                            />
                            <span className="checkmark"></span>
                          </label>

                          <div>
                            <h3 className="text-sm text-fmb font-semibold">
                              {item.item.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {item.item.category}
                            </p>

                            {typeof item.quantityReceived === 'number' ? (
                              <>
                                <p className="text-sm text-gray-400">
                                  Requested Quantity:{' '}
                                  {parseFloat(item.quantity).toFixed(2)}{' '}
                                  <span>{item.item.unit}</span>
                                </p>
                                <p className="text-sm text-green-600">
                                  Received Quantity:{' '}
                                  {parseFloat(item.quantityReceived).toFixed(2)}{' '}
                                  <span>{item.item.unit}</span>
                                </p>
                                <p className="text-sm text-red-600">
                                  Pending Quantity:{' '}
                                  {item?.quantityDue &&
                                  !isNaN(parseFloat(item.quantityDue))
                                    ? parseFloat(item.quantityDue).toFixed(2)
                                    : ' '}
                                  <span>{item.item.unit}</span>
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400">
                                Requested Quantity:{' '}
                                {parseFloat(item.quantity).toFixed(2)}{' '}
                                <span>{item.item.unit}</span>
                              </p>
                            )}

                            {getItemRemarks(item) && (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleRemarks(
                                      `request-item-${request._id}-${item._id}`
                                    )
                                  }
                                  className="text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-800"
                                >
                                  {expandedRemarks[
                                    `request-item-${request._id}-${item._id}`
                                  ]
                                    ? 'Hide item remarks'
                                    : 'View item remarks'}
                                </button>

                                {expandedRemarks[
                                  `request-item-${request._id}-${item._id}`
                                ] && (
                                  <div className="mt-1 p-2 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap">
                                    {getItemRemarks(item)}
                                  </div>
                                )}
                              </div>
                            )}

                            {checkedRequest[`${request._id}-${item._id}`] && (
                              <input
                                type="number"
                                value={parseFloat(
                                  checkedRequest[
                                    `${request._id}-${item._id}`
                                  ].quantity
                                ).toFixed(2)}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    request._id,
                                    item._id,
                                    e.target.value
                                  )
                                }
                                max={parseFloat(item.quantity).toFixed(2)}
                                className={`mt-1 p-1 border ${
                                  quantityErrors[
                                    `${request._id}-${item._id}`
                                  ]
                                    ? 'border-red-500'
                                    : 'border-gray-300'
                                } rounded`}
                              />
                            )}

                            {quantityErrors[`${request._id}-${item._id}`] && (
                              <p className="text-xs text-red-500 mt-1">
                                {quantityErrors[`${request._id}-${item._id}`]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="ml-auto flex items-center">
                          <div
                            className={`text-xs px-2 py-1 rounded text-white ${getStatusColor(
                              item.status
                            )}`}
                          >
                            {item.status}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ))
            ) : (
              <p className="text-gray-600">No requests found.</p>
            )}
          </>
        )}

        {activeTab === 'purchase-orders' && (
          <>
            <div className="mb-4 flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyPendingPOs}
                  onChange={(e) => setShowOnlyPendingPOs(e.target.checked)}
                  className="mr-2"
                />
                Show only purchase orders with pending items
              </label>
            </div>

            {showOnlyPendingPOs && (
              <p className="text-sm text-gray-600 mb-2">
                Showing only purchase orders with pending items.
              </p>
            )}

            {filteredPurchaseOrders.length > 0 ? (
              filteredPurchaseOrders.map((po, index) => (
                <div
                  key={index}
                  className="p-4 mb-3 border rounded shadow-md bg-white"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-[16px] font-semibold text-gray-700">
                      PO #00{index + 1}
                    </h2>

                    <h2 className="text-[16px] font-semibold text-gray-700">
                      PO Number:{' '}
                      {po?.masterPO?.PONumber
                        ? po.masterPO.PONumber
                        : po._id.slice(-6)}
                      <br />
                      {console.log('Zone PO ID:', po._id)}
                    </h2>

                    <div className="text-right">
                      <h4 className="text-sm text-gray-400">
                        {formatDate(po.date)}
                      </h4>
                      {po.deliveryDate && (
                        <h4 className="text-xs text-blue-600">
                          Delivery: {formatDate(po.deliveryDate)}
                        </h4>
                      )}
                      {po.grn && (
                        <h4 className="text-xs text-blue-600">
                          GRN: {po.grn}
                        </h4>
                      )}
                    </div>
                  </div>

                  {po.supplier && (
                    <p className="text-sm text-gray-600 mb-2">
                      Supplier: {po.supplier.name}
                    </p>
                  )}

                  {getRecordRemarks(po) && (
                    <div className="mb-3">
                      <button
                        type="button"
                        onClick={() => toggleRemarks(`po-${po._id}`)}
                        className="inline-flex items-center gap-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full hover:bg-blue-100"
                      >
                        <span>Remarks</span>
                        <span className="text-[10px] text-blue-600">
                          {expandedRemarks[`po-${po._id}`] ? 'Hide' : 'View'}
                        </span>
                      </button>

                      {expandedRemarks[`po-${po._id}`] && (
                        <div className="mt-2 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-gray-700 whitespace-pre-wrap">
                          {getRecordRemarks(po)}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-3 border-b pb-2 w-fit">
                    <div className="flex items-center">
                      <label className="custom-checkbox">
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            handleSelectAll(po._id, po.items, e.target.checked)
                          }
                          checked={
                            po.items
                              .filter((item) => !isFullyReceived(item))
                              .every(
                                (item) =>
                                  checkedPOItems[`${po._id}-${item._id}`]
                              ) &&
                            po.items.some((item) => !isFullyReceived(item))
                          }
                          disabled={po.items.every((item) => isFullyReceived(item))}
                        />
                        <span className="checkmark"></span>
                      </label>
                      <span className="ml-2 text-sm font-semibold text-green-600 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Select All
                      </span>
                    </div>
                  </div>

                  {po.items
                    .filter((item) =>
                      item.item.name
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    )
                    .map((item, idx, array) => (
                      <div
                        key={idx}
                        className={`flex items-center mb-2 ${
                          idx < array.length - 1 ? 'border-b pb-2' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <label className="custom-checkbox">
                            <input
                              type="checkbox"
                              disabled={isFullyReceived(item)}
                              checked={
                                checkedPOItems[`${po._id}-${item._id}`] !==
                                undefined
                              }
                              onChange={(e) =>
                                handlePOCheckboxChange(
                                  po._id,
                                  item,
                                  e.target.checked
                                )
                              }
                            />
                            <span className="checkmark"></span>
                          </label>

                          <div>
                            <h3 className="text-sm text-fmb font-semibold">
                              {item.item.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {item.item.category}
                            </p>

                            {typeof item.quantityReceived === 'number' ? (
                              <>
                                <p className="text-sm text-gray-400">
                                  Requested Quantity:{' '}
                                  {parseFloat(item.quantity).toFixed(2)}{' '}
                                  <span>{item.item.unit}</span>
                                </p>
                                <p className="text-sm text-green-600">
                                  Received Quantity: {n2(item.quantityReceived)}{' '}
                                  <span>{item.item.unit}</span>
                                </p>
                                <p className="text-sm text-red-600">
                                  Pending Quantity:{' '}
                                  {Math.max(
                                    0,
                                    Number(item.quantity || 0) -
                                      Number(item.quantityReceived || 0)
                                  ).toFixed(2)}{' '}
                                  <span>{item.item.unit}</span>
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400">
                                Requested Quantity:{' '}
                                {parseFloat(item.quantity).toFixed(2)}{' '}
                                <span>{item.item.unit}</span>
                              </p>
                            )}

                            <p className="text-sm text-gray-400">
                              Price: {formatMoney(user, item.price)} per {item.item.unit}
                            </p>

                            {getItemRemarks(item) && (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleRemarks(`po-item-${po._id}-${item._id}`)
                                  }
                                  className="text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-800"
                                >
                                  {expandedRemarks[`po-item-${po._id}-${item._id}`]
                                    ? 'Hide item remarks'
                                    : 'View item remarks'}
                                </button>

                                {expandedRemarks[`po-item-${po._id}-${item._id}`] && (
                                  <div className="mt-1 p-2 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap">
                                    {getItemRemarks(item)}
                                  </div>
                                )}
                              </div>
                            )}

                            {checkedPOItems[`${po._id}-${item._id}`] && (
                              <input
                                type="number"
                                value={
                                  checkedPOItems[`${po._id}-${item._id}`].quantity
                                }
                                onChange={(e) =>
                                  handlePOQuantityChange(
                                    po._id,
                                    item._id,
                                    e.target.value
                                  )
                                }
                                min="0"
                                step="0.01"
                                onWheel={(e) => e.target.blur()}
                                placeholder="Enter quantity"
                                className={`mt-1 p-1 border ${
                                  quantityErrors[`${po._id}-${item._id}`]
                                    ? 'border-red-500'
                                    : 'border-gray-300'
                                } rounded`}
                              />
                            )}

                            {quantityErrors[`${po._id}-${item._id}`] && (
                              <p className="text-xs text-red-500 mt-1">
                                {quantityErrors[`${po._id}-${item._id}`]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="ml-auto flex items-center">
                          <div
                            className={`text-xs px-2 py-1 rounded text-white ${getStatusColor(
                              item.status
                            )}`}
                          >
                            {item.status}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ))
            ) : (
              <p className="text-gray-600">No purchase orders found.</p>
            )}
          </>
        )}

        {activeTab === 'silat-items' &&
          ((user?.role && user.role.toLowerCase() === 'admin') ||
            (user?.zone?.desc &&
              user.zone.desc.toLowerCase() === 'central zone')) && (
            <SilatItemsIn user={user} navigate={navigate} />
          )}

        {isBannerVisible && (
          <div
            className={`fixed bottom-0 pb-20 left-0 right-0 p-4 bg-yellow-100 shadow-lg ${
              totalCheckedItems > 0 ? 'animate-slideIn' : 'animate-slideOut'
            }`}
          >
            {successMessage && <div style={{ color: 'green' }}>{successMessage}</div>}
            {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}

            <div className="mb-3">
              {!showRemarksInput ? (
                <button
                  type="button"
                  onClick={() => setShowRemarksInput(true)}
                  className="text-sm text-lime-800 font-medium underline underline-offset-2"
                >
                  Add Remarks (Optional)
                </button>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Remarks
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRemarksInput(false);
                        setRemarks('');
                      }}
                      className="text-xs text-gray-500 hover:text-red-500"
                    >
                      Remove
                    </button>
                  </div>

                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    maxLength={300}
                    placeholder="Add any notes for this item check-in"
                    className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {remarks.length}/300 characters
                  </p>
                </div>
              )}
            </div>

            {activeTab === 'purchase-orders' && checkedPOItemsCount > 0 && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GRN Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={grnNumber}
                  onChange={(e) => setGrnNumber(e.target.value)}
                  placeholder="Enter GRN number"
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                />
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-[14px] font-semibold">
                {totalCheckedItems} items selected
                {checkedRequestItemsCount > 0 &&
                  checkedPOItemsCount > 0 &&
                  ` (${checkedRequestItemsCount} from requests, ${checkedPOItemsCount} from POs)`}
              </span>

              <button
                className={`text-[15px] bg-fmb text-white px-4 py-2 rounded font-medium ${
                  isButtonDisabled ? 'bg-gray-400 text-gray-100' : ''
                }`}
                onClick={addAndUpdate}
                disabled={isButtonDisabled}
              >
                Add Items to Stock
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ItemIn;
